<script setup>
import SignupForm from './components/Signup/Form.vue';
import SiteFooter from '../SiteFooter.vue';

defineProps({
  // Pre-fills the email field when the user arrives via "Change email address"
  // on the verify-email page (or with a ?email= query).
  initialEmail: { type: String, default: '' },
});

const FEATURES_INCLUDED = [
  'Manage multiple chat accounts',
  'Assign agents to an account or chat',
  'Upload media directly in chat',
  'Send pre-recorded audios as voice messages',
  'Fangate vault integration',
  'Built-in CRM with contact tags & notes',
  'Email support',
];
</script>

<template>
  <div class="cc-signup-page">
    <div class="cc-signup-noise" />

    <header class="cc-signup-header">
      <div class="cc-brand-mark">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="38"
          height="38"
          viewBox="0 0 96 92"
          class="cc-brand-icon"
        >
          <defs>
            <linearGradient id="goldgrad-su" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0" stop-color="#d8b86a" />
              <stop offset="0.5" stop-color="#a8842f" />
              <stop offset="1" stop-color="#7a5a14" />
            </linearGradient>
            <clipPath id="clipover-su">
              <rect x="38" y="44" width="20" height="22" />
            </clipPath>
          </defs>
          <path
            d="M 11 0 L 33 0 A 11 11 0 0 1 44 11 L 44 25 A 11 11 0 0 1 33 36 L 24 36 L 7 47 L 12 36 L 11 36 A 11 11 0 0 1 0 25 L 0 11 A 11 11 0 0 1 11 0 Z"
            transform="translate(7,11)"
            fill="none"
            stroke="#0d9488"
            stroke-width="7"
            stroke-linejoin="round"
          />
          <g transform="translate(7,11)" fill="#0d9488">
            <circle cx="15" cy="18" r="2.4" />
            <circle cx="24" cy="18" r="2.4" />
            <circle cx="33" cy="18" r="2.4" />
          </g>
          <path
            d="M 11 0 L 33 0 A 11 11 0 0 1 44 11 L 44 25 A 11 11 0 0 1 33 36 L 32 36 L 37 47 L 20 36 L 11 36 A 11 11 0 0 1 0 25 L 0 11 A 11 11 0 0 1 11 0 Z"
            transform="translate(41,30)"
            fill="none"
            stroke="url(#goldgrad-su)"
            stroke-width="7"
            stroke-linejoin="round"
          />
          <g transform="translate(41,30)" fill="#a8842f">
            <circle cx="15" cy="18" r="2.4" />
            <circle cx="24" cy="18" r="2.4" />
            <circle cx="33" cy="18" r="2.4" />
          </g>
          <g clip-path="url(#clipover-su)">
            <path
              d="M 11 0 L 33 0 A 11 11 0 0 1 44 11 L 44 25 A 11 11 0 0 1 33 36 L 24 36 L 7 47 L 12 36 L 11 36 A 11 11 0 0 1 0 25 L 0 11 A 11 11 0 0 1 11 0 Z"
              transform="translate(7,11)"
              fill="none"
              stroke="#0d9488"
              stroke-width="7"
              stroke-linejoin="round"
            />
          </g>
        </svg>
        <div class="cc-brand-text">
          <div class="cc-wordmark">
            <span class="cc-wordmark-part cc-wordmark-creator">Creator</span><span class="cc-wordmark-dash">-</span><span class="cc-wordmark-part cc-wordmark-chat">Chat</span><span class="cc-wordmark-tld">.com</span>
          </div>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="90"
            height="2"
            viewBox="0 0 90 2"
            class="cc-brand-divider"
          >
            <defs>
              <linearGradient id="tealfade-su" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stop-color="#0d9488" stop-opacity="0" />
                <stop offset="20%" stop-color="#0d9488" stop-opacity="1" />
                <stop offset="80%" stop-color="#0d9488" stop-opacity="1" />
                <stop offset="100%" stop-color="#0d9488" stop-opacity="0" />
              </linearGradient>
            </defs>
            <rect x="0" y="0" width="90" height="2" fill="url(#tealfade-su)" />
          </svg>
          <div class="cc-tagline">Conversations · Tailored</div>
        </div>
      </div>

      <div class="cc-signin-link">
        Already have an account?
        <router-link to="/app/login">Sign in →</router-link>
      </div>
    </header>

    <main class="cc-signup-main">
      <div class="cc-signup-grid">
        <!-- LEFT: Form -->
        <div class="cc-form-col">
          <div class="cc-form-intro">
            <span class="cc-pill cc-pill-free">
              <span class="cc-pill-dot" />
              FREE · No credit card required
            </span>
            <h1 class="cc-h-display">
              Get your team chatting<br />in under 3 minutes.
            </h1>
            <p class="cc-lede">
              Centralize Telegram conversations, sell PPV directly within your chats and manage everything in one place.
            </p>
          </div>

          <SignupForm :initial-email="initialEmail" />
        </div>

        <!-- RIGHT: Plan summary -->
        <aside class="cc-plan-col">
          <div class="cc-card cc-plan-card">
            <div class="cc-plan-head">
              <div>
                <span class="cc-pill cc-pill-free">
                  <span class="cc-pill-dot" />
                  Free plan
                </span>
                <div class="cc-h-display cc-plan-name">FREE</div>
              </div>
              <div class="cc-plan-price">
                <div class="cc-plan-price-amt">€0</div>
                <div class="cc-plan-price-unit">per month</div>
              </div>
            </div>

            <div class="cc-plan-note">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#0f766e"
                stroke-width="2"
                stroke-linecap="round"
              >
                <circle cx="12" cy="12" r="10" />
                <path d="M9 12l2 2 4-4" />
              </svg>
              <div>No credit card required. Get started in minutes.</div>
            </div>

            <div class="cc-section-label">What's included</div>
            <div>
              <div
                v-for="(feature, idx) in FEATURES_INCLUDED"
                :key="idx"
                class="cc-feature-row"
              >
                <div class="cc-feature-check">
                  <svg
                    width="9"
                    height="9"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="3.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </div>
                <div>{{ feature }}</div>
              </div>
            </div>
          </div>

          <div class="cc-trust-strip">
            <div class="cc-avatars">
              <div class="cc-avatar cc-avatar-1">ME</div>
              <div class="cc-avatar cc-avatar-2">LV</div>
              <div class="cc-avatar cc-avatar-3">JH</div>
            </div>
            <div class="cc-trust-text">
              <strong>47 agencies</strong> already managing creators on Creator-Chat.
            </div>
          </div>
        </aside>
      </div>
    </main>

    <SiteFooter />
  </div>
</template>

<style scoped>
.cc-signup-page {
  position: relative;
  min-height: 100vh;
  width: 100%;
  background: #efeae0;
  color: #0b1414;
  font-family: 'Inter', sans-serif;
  display: flex;
  flex-direction: column;
}
.cc-signup-noise {
  position: fixed;
  inset: 0;
  pointer-events: none;
  z-index: 0;
  opacity: 0.35;
  mix-blend-mode: multiply;
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0.4 0 0 0 0 0.35 0 0 0 0 0.25 0 0 0 0.05 0'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>");
}

.cc-signup-header {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 22px 36px;
}
.cc-signup-header .cc-signin-link {
  position: absolute;
  right: 36px;
  top: 50%;
  transform: translateY(-50%);
}

.cc-brand-mark {
  display: flex;
  align-items: center;
}
.cc-brand-icon {
  display: block;
  margin-right: 14px;
}
.cc-brand-text { line-height: 1; }
.cc-wordmark {
  line-height: 21px;
  white-space: nowrap;
}
.cc-wordmark-part {
  font-family: 'Fraunces', 'Times New Roman', Georgia, serif;
  font-size: 21px;
  letter-spacing: 1.26px;
  text-transform: uppercase;
  color: #0b1414;
}
.cc-wordmark-creator { font-weight: 400; }
.cc-wordmark-chat { font-weight: 300; }
.cc-wordmark-dash {
  font-family: 'Fraunces', 'Times New Roman', Georgia, serif;
  font-size: 21px;
  letter-spacing: 1.26px;
  color: #a8842f;
  font-weight: 400;
}
.cc-wordmark-tld {
  font-family: 'Inter', 'Helvetica Neue', Arial, sans-serif;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: lowercase;
  color: #a8842f;
  vertical-align: 8px;
  padding-left: 2px;
}
.cc-brand-divider {
  display: block;
  margin: 6px 0 5px 0;
}
.cc-tagline {
  font-family: 'Inter', 'Helvetica Neue', Arial, sans-serif;
  font-size: 8.5px;
  font-weight: 500;
  letter-spacing: 2.56px;
  color: rgba(11, 20, 20, 0.5);
  text-transform: uppercase;
  line-height: 1;
}

.cc-signin-link {
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  color: #6e7570;
}
.cc-signin-link a {
  color: #0f766e;
  font-weight: 700;
  text-decoration: none;
  margin-left: 4px;
}
.cc-signin-link a:hover { text-decoration: underline; }

.cc-signup-main {
  position: relative;
  z-index: 1;
  flex: 1;
  max-width: 1100px;
  width: 100%;
  margin: 0 auto;
  padding: 30px 24px 60px;
  box-sizing: border-box;
}
.cc-signup-grid {
  display: grid;
  grid-template-columns: 1.05fr 1fr;
  gap: 48px;
  align-items: start;
}

.cc-form-col { min-width: 0; }
.cc-form-intro { margin-bottom: 28px; }

.cc-pill {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 11px;
  border-radius: 999px;
  font-family: 'Inter', sans-serif;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.02em;
}
.cc-pill-free {
  background: rgba(13, 148, 136, 0.22);
  color: #0f766e;
}
.cc-pill-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
}

.cc-h-display {
  font-family: 'Cinzel', serif;
  font-weight: 600;
  color: #0b1414;
  letter-spacing: -0.005em;
  font-size: 34px;
  line-height: 1.15;
  margin: 14px 0 10px;
}
.cc-lede {
  font-family: 'Inter', sans-serif;
  font-size: 14.5px;
  color: #6e7570;
  line-height: 1.55;
  max-width: 480px;
  margin: 0;
}

.cc-plan-col {
  position: sticky;
  top: 30px;
}
.cc-card {
  background: #ffffff;
  border: 1px solid #e0d8c5;
  border-radius: 16px;
}
.cc-plan-card { padding: 26px 28px; }
.cc-plan-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 18px;
}
.cc-plan-name {
  font-size: 22px;
  margin-top: 6px;
  margin-bottom: 0;
}
.cc-plan-price { text-align: right; }
.cc-plan-price-amt {
  font-family: 'Inter', sans-serif;
  font-size: 32px;
  font-weight: 800;
  color: #0b1414;
  line-height: 1;
}
.cc-plan-price-unit {
  font-family: 'Inter', sans-serif;
  font-size: 11px;
  color: #6e7570;
  margin-top: 2px;
}

.cc-plan-note {
  background: #f1ede4;
  border-radius: 10px;
  padding: 12px 14px;
  margin-bottom: 18px;
  display: flex;
  align-items: center;
  gap: 10px;
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  color: #2a3434;
  line-height: 1.4;
}
.cc-plan-note svg { flex-shrink: 0; }

.cc-section-label {
  font-family: 'Cinzel', serif;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #0b1414;
  margin-bottom: 8px;
}

.cc-feature-row {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 8px 0;
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  color: #2a3434;
  line-height: 1.5;
}
.cc-feature-check {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: rgba(13, 148, 136, 0.22);
  color: #0f766e;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-top: 2px;
}

.cc-trust-strip {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-top: 20px;
  padding: 0 6px;
}
.cc-avatars { display: flex; }
.cc-avatar {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'Inter', sans-serif;
  font-size: 11px;
  font-weight: 700;
  border: 2px solid #efeae0;
}
.cc-avatar-1 {
  background: rgba(13, 148, 136, 0.22);
  color: #0f766e;
}
.cc-avatar-2 {
  background: rgba(168, 132, 47, 0.2);
  color: #7a5a14;
  margin-left: -9px;
}
.cc-avatar-3 {
  background: rgba(31, 42, 42, 0.85);
  color: #f5efe0;
  margin-left: -9px;
}
.cc-trust-text {
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  color: #6e7570;
  line-height: 1.4;
}
.cc-trust-text strong { color: #0b1414; }

/* Footer styles live in ../SiteFooter.vue (scoped). */

@media (max-width: 880px) {
  .cc-signup-grid {
    grid-template-columns: 1fr;
    gap: 32px;
  }
  .cc-plan-col {
    position: static;
  }
  .cc-h-display { font-size: 26px; }
}

@media (max-width: 540px) {
  .cc-signup-header {
    padding: 18px 20px;
    flex-direction: column;
    gap: 12px;
  }
  .cc-signup-header .cc-signin-link {
    position: static;
    transform: none;
  }
  .cc-signup-main { padding: 20px 16px 40px; }
  .cc-tagline { display: none; }
}
</style>
